import { List, Map, Set } from 'immutable';
import comm from '../../comm/actions';
import {
  FORM_SET_REMOTE_VALIDATIONS,
  FORM_REMOVE_MULTIFIELD_ROW,
  FORM_ADD_MULTIFIELD_ROW,
  FORM_SET_SUBMIT_STATE,
  FORM_COPY_FIELD_DATA,
  FORM_SET_FIELD_FOCUS,
  FORM_SET_FIELD_STATE,
  FORM_SET_FIELD_TYPE,
  FORM_FORCE_VALIDATE,
  FORM_SET_FIELD_STEP,
  FORM_SET_VALIDATOR,
  FORM_SHOW_ERRORS,
  FORM_INIT_FIELD,
  FORM_SET_VALUES,
  FORM_VALIDATE,
  FORM_MERGE_FORMS,
  FORM_CLEAN,
  FORM_MERGE,
  NAMESPACE,
} from './consts';

function clean(view, form) {
  return {
    namespace: NAMESPACE,
    type: FORM_CLEAN,
    view,
    form,
  };
}

function validate(view, form) {
  return {
    namespace: NAMESPACE,
    type: FORM_VALIDATE,
    view,
    form,
  };
}

function setValues(view, form, values) {
  return {
    namespace: NAMESPACE,
    type: FORM_SET_VALUES,
    view,
    form,
    values,
  };
}

function merge(view, form, values) {
  return {
    namespace: NAMESPACE,
    type: FORM_MERGE,
    view,
    form,
    values,
  };
}

function setRemoteValidations(view, form, validations) {
  return {
    namespace: NAMESPACE,
    type: FORM_SET_REMOTE_VALIDATIONS,
    view,
    form,
    validations,
  };
}

function setValidator(view, form, validator = () => null) {
  let v = () => null;
  if (validator) {
    if (typeof validator === 'object') {
      v = validator.validator;
    } else {
      v = validator;
    }
  }
  return {
    namespace: NAMESPACE,
    type: FORM_SET_VALIDATOR,
    view,
    form,
    validator: v,
  };
}

function setSubmitState(view, form) {
  return {
    namespace: NAMESPACE,
    type: FORM_SET_SUBMIT_STATE,
    view,
    form,
  };
}

function showErrors(view, form, show) {
  return {
    namespace: NAMESPACE,
    type: FORM_SHOW_ERRORS,
    view,
    form,
    show,
  };
}

function setFieldStep(view, form, { field, step }) {
  return {
    namespace: NAMESPACE,
    type: FORM_SET_FIELD_STEP,
    view,
    form,
    field,
    step,
  };
}

function forceValidate(view, form) {
  return {
    namespace: NAMESPACE,
    type: FORM_FORCE_VALIDATE,
    view,
    form,
  };
}

function countMultifieldErrors(acc, m) {
  if (acc > 0) {
    return acc;
  }
  return (
    acc +
    m.reduce((reduction, e) => {
      if (reduction > 0) {
        return reduction;
      }
      return reduction + (e && e.length > 0 ? 1 : 0);
    }, 0)
  );
}

function countErrors(acc, value) {
  if (acc > 0 || !value) {
    return acc;
  }
  if (typeof value === 'string' && value.length > 0) {
    return acc + 1;
  }
  return acc + value.reduce(countMultifieldErrors, 0);
}

function getErrorsAndCount(getState, view, form) {
  const errorsKeyPath = ['views', view, 'forms', form, 'errors'];
  const errors = getState().getIn(errorsKeyPath) || Map();
  return [errors, errors.reduce(countErrors, 0)];
}

/**
 * Incrementa o Request.
 *
 * Preenche as propriedades `requiredValidation` e `data`.
 * @param {Request} request
 * @param {string} form
 * @param {Map} data
 * @param {Map} errors
 */
function augmentRequest(request, form, data, errors) {
  const fields = data.keySeq();
  const errFields = errors.keySeq();
  if ((!fields || fields.size < 1) && (!errFields || errFields.size < 1)) {
    return request;
  }

  const view = request.get('view');
  const vfn = (validations) => setRemoteValidations(view, form, validations);
  const dispatch = request.getIn([
    'onError',
    'requiredValidations',
    'dispatch',
  ]);

  let newDispatch = null;
  if (typeof dispatch === 'function') {
    newDispatch = List([dispatch, vfn]);
  } else if (!dispatch) {
    newDispatch = vfn;
  } else {
    newDispatch = dispatch.push(vfn);
  }
  // o campos de requisições requeridas são a união das chaves
  // nos campos do formulário com as chaves dos erros dos dele,
  // pois campos podem não estar montados na hora da submissão
  // (em tabs e steppers, por exemplo) e, dependendo da imple-
  // mentação, a função de validação pode não retornar as cha-
  // ves de campos sem erro.
  const requiredFields = Set(fields).union(errFields);
  return request
    .set('data', data)
    .setIn(['onError', 'requiredValidations', 'fields'], requiredFields)
    .setIn(['onError', 'requiredValidations', 'dispatch'], newDispatch);
}

function dispatchOnFail(dispatch, onFail, errors) {
  if (!onFail) {
    return;
  }
  if (typeof onFail === 'function') {
    const d = onFail(errors);
    if (d) {
      dispatch(d);
    }
    return;
  }

  for (let i = 0; i < onFail.size; i += 1) {
    const fn = onFail.get(i);
    const d = fn(errors);
    if (d) {
      dispatch(d);
    }
  }
}

function submit(form, submit) {
  return (dispatch, getState) => {
    const method = submit.get('method');
    const request = submit.get('request');
    if (method !== 'put' && method !== 'post') {
      console.error(
        `Método "${method}" inválido para submissão.` +
          ` Tem que ser "post" ou "put"`,
      );
      return null;
    }

    const view = request.get('view');
    const formState = getState().getIn(['views', view, 'forms', form]) || Map();
    const data = formState.get('data');
    dispatch(validate(view, form));
    dispatch(setSubmitState(view, form));

    const [errors, errorCount] = getErrorsAndCount(getState, view, form);
    if (errorCount > 0 /* TODO || data.size < 1 */) {
      const onFail = submit.get('onFail');
      if (onFail) {
        dispatchOnFail(dispatch, onFail, errors);
      }
      return null;
    }

    const willSubmit = submit.get('willSubmit');
    if (willSubmit) {
      const r = willSubmit();
      if (r) {
        dispatch(r);
      }
    }

    const req = augmentRequest(request, form, data, errors);
    if (method === 'post') {
      return comm.post(req)(dispatch, getState);
    }
    return comm.put(req)(dispatch, getState);
  };
}

function mergeForms(view, formA, formB) {
  return {
    namespace: NAMESPACE,
    type: FORM_MERGE_FORMS,
    view,
    formA,
    formB,
  };
}

function initField(view, form, args) {
  const { type, field, index, multifield, defaultValue, step } = args;

  return {
    namespace: NAMESPACE,
    type: FORM_INIT_FIELD,
    view,
    form,
    fieldType: type,
    field,
    index,
    multifield,
    defaultValue,
    step,
  };
}

function setFieldState(view, form, args) {
  const { field, value, index, multifield } = args;
  return {
    namespace: NAMESPACE,
    type: FORM_SET_FIELD_STATE,
    view,
    form,
    field,
    value,
    index,
    multifield,
  };
}

function setFieldType(view, form, args) {
  const { field, type, index, multifield } = args;
  return {
    namespace: NAMESPACE,
    type: FORM_SET_FIELD_TYPE,
    view,
    form,
    field,
    index,
    multifield,
    fieldType: type,
  };
}

function copyFieldData(view, form, from, to) {
  return {
    namespace: NAMESPACE,
    type: FORM_COPY_FIELD_DATA,
    view,
    form,
    from,
    to,
  };
}

function setFieldFocusAndValidate(view, form, args) {
  const { field, isFocused, index, multifield, disabled } = args;
  return {
    namespace: NAMESPACE,
    type: FORM_SET_FIELD_FOCUS,
    view,
    form,
    field,
    isFocused,
    index,
    multifield,
    disabled,
  };
}

function removeMultifieldRow(view, form, multifield, index) {
  return {
    namespace: NAMESPACE,
    type: FORM_REMOVE_MULTIFIELD_ROW,
    view,
    form,
    multifield,
    index,
  };
}

function addMultifieldRow(view, form, multifield, index) {
  return {
    namespace: NAMESPACE,
    type: FORM_ADD_MULTIFIELD_ROW,
    view,
    form,
    multifield,
    index,
  };
}

const actions = {
  setFieldFocusAndValidate,
  setRemoteValidations,
  removeMultifieldRow,
  addMultifieldRow,
  setFieldState,
  copyFieldData,
  forceValidate,
  setFieldType,
  setFieldStep,
  setValidator,
  showErrors,
  mergeForms,
  setValues,
  initField,
  validate,
  submit,
  clean,
  merge,
};

export default actions;
